import { cn } from '@/lib/utils';
import { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, MessageCircle, Bot } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLocation, Link } from 'react-router-dom';
import heroLogo from '@/assets/hero-logo.png';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface HeroProps {
  title: string;
  subtitle?: string;
  image: string;
  overlayOpacity?: number;
  height?: 'small' | 'medium' | 'large' | 'full';
  position?: 'center' | 'top' | 'bottom';
  className?: string;
  rotatingTexts?: string[];
  darkText?: boolean;
  showLogo?: boolean;
}

const Hero = ({
  title,
  subtitle,
  image,
  overlayOpacity = 0.95,
  height = 'large',
  position = 'center',
  className,
  rotatingTexts = [],
  darkText = true,
  showLogo
}: HeroProps) => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const isMobile = useIsMobile();
  const location = useLocation();
  
  // Determine if we should show the logo based on the prop or automatically based on homepage
  const shouldShowLogo = showLogo !== undefined ? showLogo : location.pathname === '/';

  const heightClasses = {
    small: 'h-[40vh]',
    medium: 'h-[60vh]',
    large: 'h-[80vh]',
    full: 'h-screen'
  };

  const positionClasses = {
    center: 'bg-center',
    top: 'bg-top',
    bottom: 'bg-bottom'
  };

  useEffect(() => {
    if (rotatingTexts.length <= 1) return;

    const interval = setInterval(() => {
      setIsAnimating(true);
      
      // Change text after exit animation completes
      setTimeout(() => {
        setCurrentTextIndex((prevIndex) => (prevIndex + 1) % rotatingTexts.length);
        setIsAnimating(false);
      }, 500); // 500ms for the exit animation
      
    }, 2000); // Change text every 2 seconds

    return () => clearInterval(interval);
  }, [rotatingTexts.length]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToNextSection = () => {
    const aboutSection = document.querySelector('#about-section');
    if (aboutSection) {
      aboutSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const openWhatsApp = () => {
    window.open(`https://wa.me/972529388054`, '_blank');
  };

  const textColorClass = darkText ? "text-black" : "text-white";

  return (
    <div 
      className={cn(
        "relative w-full bg-cover bg-no-repeat",
        heightClasses[height],
        positionClasses[position],
        className
      )}
      style={{ 
        backgroundImage: `url(${image})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-white"
        style={{ opacity: overlayOpacity }}
      ></div>
      
      {/* Bottom fade gradient for smooth transition */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent to-white z-[5]"
      ></div>
      
      {/* Content container - Moved higher for desktop */}
      <div className={cn(
        "absolute inset-0 flex flex-col justify-center items-center text-center p-4 z-10",
        shouldShowLogo ? isMobile ? "-mt-36 md:mt-0" : "md:-mt-24" : ""
      )}>
        {/* Logo section - only shown on homepage */}
        {shouldShowLogo ? (
          <div className={cn(
            "mb-8",
            isMobile ? "" : "md:flex md:flex-col md:items-center md:justify-center md:space-y-2"
          )}>
            <img 
              src={heroLogo} 
              alt="Smirnov Zooler Law Firm" 
              className="w-auto h-32 md:h-40 mx-auto fade-in"
            />
            
            {/* Hebrew text section - Moved higher for desktop */}
            <div className={cn(
              "mt-8 relative",
              isMobile ? "" : "md:mt-6 md:max-w-2xl md:mx-auto"
            )}>
              <div className={cn(
                "font-handwriting rtl relative z-10",
                "py-4 px-6",
              )}>
                <p className="text-3xl md:text-4xl mb-3 font-semibold text-highlight tracking-wide">הסוף לחובות!</p>
                <p className="text-xl md:text-2xl font-medium text-black/80 tracking-wide">גם לך מגיע התחלה חדשה</p>
              </div>
              {/* Enhanced separator line */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[120%] h-[1px] bg-gradient-to-r from-transparent via-black/15 to-transparent z-0"></div>
            </div>
            
            {/* Rotating texts - Moved higher for desktop */}
            {rotatingTexts.length > 0 && (
              <div className={cn(
                "h-10 md:h-12 overflow-hidden my-2 mt-6",
                isMobile ? "" : "md:mt-6 md:mb-4"
              )}>
                <p 
                  className={cn(
                    "text-2xl md:text-3xl font-bold transition-all duration-500 transform",
                    isAnimating ? "opacity-0 -translate-y-8" : "opacity-100 translate-y-0",
                    textColorClass
                  )}
                >
                  {rotatingTexts[currentTextIndex]}
                </p>
              </div>
            )}
          </div>
        ) : (
          // For other pages, refined layout for titles
          <div className="md:max-w-3xl mx-auto">
            <h2 className={cn("text-4xl md:text-5xl lg:text-6xl font-light mb-4 fade-in", textColorClass)}>{title}</h2>
            {subtitle && (
              <p className={cn("text-xl md:text-2xl font-light max-w-2xl mx-auto fade-in opacity-90", textColorClass)}>
                {subtitle}
              </p>
            )}
          </div>
        )}
      </div>
      
      {/* Contact Buttons - Only on homepage, above scroll arrow */}
      {shouldShowLogo && (
        <div className="absolute bottom-32 md:bottom-36 left-1/2 transform -translate-x-1/2 z-20 flex flex-row gap-3 items-center">
          <Link to="/contact#contact-form">
            <Button 
              variant="outline" 
              className="border-black/30 text-black bg-white/60 hover:bg-black hover:text-white transition-all duration-300 backdrop-blur-sm whitespace-nowrap"
            >
              להשארת פרטים לחצו כאן
            </Button>
          </Link>
          
          <Link to="/legal-assistant">
            <Button 
              variant="outline" 
              className="border-black/30 text-black bg-white/60 hover:bg-black hover:text-white transition-all duration-300 backdrop-blur-sm whitespace-nowrap"
            >
              עוזר משפטי AI
            </Button>
          </Link>
        </div>
      )}

      {/* Scroll down button - Only on mobile homepage */}
      {isMobile && shouldShowLogo && (
        <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 z-20 flex gap-4">
          <div 
            className="cursor-pointer animate-bounce"
            onClick={scrollToNextSection}
            aria-label="גלול למטה"
          >
            <ChevronDown 
              size={36} 
              className={cn(
                "rounded-full p-1 opacity-40 hover:opacity-70 transition-opacity", 
                darkText ? "text-black/60" : "text-white/70"
              )}
            />
          </div>
        </div>
      )}

      {/* Desktop scroll down button - Moved higher */}
      {!isMobile && shouldShowLogo && (
        <div className="hidden md:flex absolute bottom-16 left-1/2 transform -translate-x-1/2 z-20">
          <div 
            className="cursor-pointer animate-bounce hover:scale-110 transition-transform"
            onClick={scrollToNextSection}
            aria-label="גלול למטה"
          >
            <ChevronDown 
              size={42} 
              className={cn(
                "rounded-full p-1 opacity-50 hover:opacity-80 transition-all", 
                darkText ? "text-black/70" : "text-white/80"
              )}
            />
          </div>
        </div>
      )}

      {/* Scroll to top button with WhatsApp button - Enhanced for better visibility */}
      {showScrollTop && (
        <div className="fixed bottom-6 left-6 z-50 flex gap-3">
          <div 
            className={cn(
              "cursor-pointer rounded-full p-2 shadow-lg transition-all duration-300 hover:scale-110",
              darkText ? "bg-black/10 hover:bg-black/15" : "bg-white/60 hover:bg-white/70"
            )}
            onClick={scrollToTop}
            aria-label="גלול לראש העמוד"
          >
            <ChevronUp 
              size={28} 
              className={darkText ? "text-black" : "text-white"}
            />
          </div>
          
          {/* WhatsApp Button - Enhanced */}
          <div 
            className={cn(
              "cursor-pointer rounded-full p-2 shadow-lg transition-all duration-300 hover:scale-110",
              darkText ? "bg-black/10 hover:bg-black/15" : "bg-white/60 hover:bg-white/70"
            )}
            onClick={openWhatsApp}
            aria-label="שלח הודעת וואטסאפ"
          >
            <MessageCircle 
              size={28} 
              className={darkText ? "text-black" : "text-white"}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Hero;
